import { t } from "@bn-games/i18n-core";
import { el } from "./dom";
import { createCrestButton } from "./CrestButton";
import { openModal } from "./Modal";
import { createAddOpponentButton, createAddOpponentRow, type OpponentIdentity } from "./AddOpponentRow";
import { getFriendIds } from "./friendIds";

export type { OpponentIdentity };

/**
 * "Add opponent to friends" for the post-game screen. `null` when there is
 * nothing to offer — every call site is expected to have already filtered
 * its roster to verified, non-self opponents, so an empty list means the
 * caller splices `null` into its existing `x ? el(...) : null`
 * children-array pattern, exactly like `createInviteFriendButton`.
 *
 * Otherwise returns a mount that stays hidden until the friends list
 * resolves: opponents the player already follows are dropped, and if none
 * remain the mount stays hidden rather than offering a pointless "Add".
 *
 * Exactly one remaining candidate renders as a single self-contained button.
 * More than one renders as a compact trigger that opens a modal listing
 * every candidate as its own row — each row sharing the same add/added/error
 * state machine as the single-candidate button (see `AddOpponentRow.ts`).
 */
export function createAddOpponentControl(opponents: readonly OpponentIdentity[]): HTMLElement | null {
  if (opponents.length === 0) return null;
  const mount = el("div", { class: "add-opponent", attrs: { hidden: "" } });
  void getFriendIds().then((friendIds) => {
    const candidates = friendIds === null
      ? opponents
      : opponents.filter((opponent) => !friendIds.has(opponent.userId));
    const control = createControlFor(candidates);
    if (control === null) return;
    mount.appendChild(control);
    mount.removeAttribute("hidden");
  });
  return mount;
}

function createControlFor(opponents: readonly OpponentIdentity[]): HTMLElement | null {
  const [first, ...rest] = opponents;
  if (first === undefined) return null;
  if (rest.length === 0) {
    return createAddOpponentButton(first);
  }

  return createCrestButton({
    label: t("social.addOpponent.buttonMany"),
    small: true,
    onClick: () => openAddOpponentsModal(opponents),
  });
}

/**
 * Stays open after each row succeeds — matching `FriendPicker.ts`'s "modal
 * stays open after each row's send" behavior — so the player can add more
 * than one just-played opponent without reopening the picker.
 */
function openAddOpponentsModal(opponents: readonly OpponentIdentity[]): void {
  const list = el("div", { class: "friend-picker__list", attrs: { "aria-live": "polite" } });
  for (const opponent of opponents) {
    list.appendChild(createAddOpponentRow(opponent));
  }

  const modal = openModal({
    title: t("social.addOpponent.modalTitle"),
    body: list,
    actions: [{ label: t("social.close"), onClick: () => modal.close() }],
  });
}
